import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Send, Plus, Trash2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

export default function SalesforceWebhook() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [settings, setSettings] = useState({
    enabled: false,
    webhook_url: '',
    http_method: 'POST',
    headers: [
      { key: 'Content-Type', value: 'application/json' }
    ],
    timeout_seconds: 30,
    retry_attempts: 3
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/salesforce-webhook-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching webhook settings:', error);
      toast.error('Failed to load webhook settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (settings.enabled && !settings.webhook_url) {
      toast.error('Please enter a webhook URL');
      return;
    }

    if (settings.enabled && settings.headers.length === 0) {
      toast.error('Please add at least one header (Content-Type is required)');
      return;
    }

    setSaving(true);
    try {
      await apiClient.put('/admin/salesforce-webhook-settings', settings);
      toast.success('Webhook settings saved successfully!');
    } catch (error) {
      console.error('Error saving webhook settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save webhook settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    if (!settings.webhook_url) {
      toast.error('Please enter a webhook URL first');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const testData = {
        source: "circuit_website",
        form_type: "test_form",
        timestamp: new Date().toISOString(),
        lead: {
          name: "Test User",
          email: "test@example.com",
          phone: "9876543210",
          company: "Test Company",
          location: "Sarjapur",
          workspace_type: "Private Office",
          seats_required: 5,
          message: "This is a test webhook from The Circuit admin panel"
        }
      };

      const response = await apiClient.post('/admin/salesforce-webhook-test', {
        webhook_url: settings.webhook_url,
        http_method: settings.http_method,
        headers: settings.headers,
        test_data: testData
      });

      setTestResult({
        success: true,
        status_code: response.data.status_code,
        message: response.data.message,
        response_body: response.data.response_body
      });
      toast.success('Webhook test successful!');
    } catch (error) {
      console.error('Error testing webhook:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Webhook test failed',
        error: error.message
      });
      toast.error('Webhook test failed');
    } finally {
      setTesting(false);
    }
  };

  const addHeader = () => {
    setSettings({
      ...settings,
      headers: [...settings.headers, { key: '', value: '' }]
    });
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...settings.headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setSettings({ ...settings, headers: newHeaders });
  };

  const removeHeader = (index) => {
    const newHeaders = settings.headers.filter((_, i) => i !== index);
    setSettings({ ...settings, headers: newHeaders });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Salesforce Webhook Integration</h1>
              <p className="text-gray-600 mt-1">Configure webhook to send lead data to Salesforce or any CRM</p>
            </div>
            <Button
              onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
              className={settings.enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'}
            >
              {settings.enabled ? 'Enabled' : 'Disabled'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Webhook URL */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Webhook Configuration</h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Webhook URL *</Label>
                  <Input
                    type="url"
                    value={settings.webhook_url}
                    onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                    placeholder="https://your-salesforce-instance.com/services/apexrest/webhook"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your Salesforce webhook URL or middleware endpoint
                  </p>
                </div>

                <div>
                  <Label>HTTP Method</Label>
                  <select
                    value={settings.http_method}
                    onChange={(e) => setSettings({ ...settings, http_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Timeout (seconds)</Label>
                    <Input
                      type="number"
                      value={settings.timeout_seconds}
                      onChange={(e) => setSettings({ ...settings, timeout_seconds: parseInt(e.target.value) || 30 })}
                      min="5"
                      max="120"
                    />
                  </div>
                  <div>
                    <Label>Retry Attempts</Label>
                    <Input
                      type="number"
                      value={settings.retry_attempts}
                      onChange={(e) => setSettings({ ...settings, retry_attempts: parseInt(e.target.value) || 3 })}
                      min="1"
                      max="5"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Headers */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">HTTP Headers</h3>
                <Button onClick={addHeader} size="sm" className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} className="mr-1" />
                  Add Header
                </Button>
              </div>

              <div className="space-y-3">
                {settings.headers.map((header, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <Input
                        value={header.key}
                        onChange={(e) => updateHeader(index, 'key', e.target.value)}
                        placeholder="Header Key (e.g., Authorization)"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={header.value}
                        onChange={(e) => updateHeader(index, 'value', e.target.value)}
                        placeholder="Header Value"
                      />
                    </div>
                    <Button
                      onClick={() => removeHeader(index)}
                      size="sm"
                      variant="destructive"
                      disabled={settings.headers.length === 1}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Common Headers:</strong>
                  <br />
                  • Content-Type: application/json (required)
                  <br />
                  • Authorization: Bearer YOUR_TOKEN
                  <br />
                  • X-API-Key: YOUR_API_KEY
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar - Payload Format & Test */}
          <div className="space-y-6">
            {/* Payload Format */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Payload Format</h3>
              <div className="text-xs bg-gray-900 text-green-400 p-4 rounded overflow-x-auto">
                <pre>{JSON.stringify({
                  source: "circuit_website",
                  form_type: "<string>",
                  timestamp: "<ISO_8601>",
                  lead: {
                    name: "<string>",
                    email: "<string>",
                    phone: "<string>",
                    company: "<string>",
                    location: "<string>",
                    workspace_type: "<string>",
                    seats_required: "<number>",
                    message: "<string>"
                  }
                }, null, 2)}</pre>
              </div>
            </div>

            {/* Test Webhook */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Test Webhook</h3>
              <Button
                onClick={handleTestWebhook}
                disabled={testing || !settings.webhook_url}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {testing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Send Test Webhook
                  </>
                )}
              </Button>

              {testResult && (
                <div className={`mt-4 p-3 rounded border ${
                  testResult.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-2">
                    {testResult.success ? (
                      <CheckCircle className="text-green-600 mt-0.5" size={18} />
                    ) : (
                      <XCircle className="text-red-600 mt-0.5" size={18} />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        testResult.success ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {testResult.message}
                      </p>
                      {testResult.status_code && (
                        <p className="text-xs text-gray-600 mt-1">
                          Status: {testResult.status_code}
                        </p>
                      )}
                      {testResult.response_body && (
                        <p className="text-xs text-gray-600 mt-1 max-h-20 overflow-y-auto">
                          Response: {testResult.response_body}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Applied Forms */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Applied To</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Contact Form
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Popup Form
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  Enquiry Modal
                </li>
              </ul>
              <p className="text-xs text-gray-500 mt-3">
                Webhook is automatically triggered when any of these forms are submitted.
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 px-8"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
